/**
 * FarmPacks — the welcome offer, behind the "10% off" bookmark at the bottom right.
 *
 * Nothing opens on its own: the "10% off" bookmark is there from the first
 * visit and the offer opens when it is clicked: the plain 10% sign-up, or the
 * prize wheel if it is switched on in Theme settings.
 *
 * Subscribing goes through Shopify's own customer form, submitted normally so
 * Shopify's captcha can run. The page reloads and the offer picks up where it
 * left off: the spin for the wheel, the code for the 10% sign-up.
 *
 * State lives in this browser only; it decides what opens, not who may use
 * a code. The prize is drawn at sign-up and tagged on the customer
 * ("wheel-15", the 10% sign-up "wheel-10"); in Shopify each code is limited
 * to customers with its tag, first order only, once each.
 */
(function () {
  var STATE_KEY = 'fp-offer-v1';
  var LEGACY_KEY = 'fp-popup-v2';
  var PENDING_KEY = 'fp-offer-pending';

  var load = function () {
    try {
      var saved = JSON.parse(localStorage.getItem(STATE_KEY) || 'null');
      if (saved && saved.state) return saved;
      // Claimed the old 10% offer: already on the list, nothing to show.
      if (localStorage.getItem(LEGACY_KEY) === 'claimed') return { state: 'legacy' };
    } catch (error) { /* private window */ }
    return { state: 'new' };
  };

  var save = function (data) {
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(data));
    } catch (error) { /* private window */ }
  };

  var randomUnit = function () {
    if (window.crypto && window.crypto.getRandomValues) {
      var buffer = new Uint32Array(1);
      window.crypto.getRandomValues(buffer);
      return buffer[0] / 4294967296;
    }
    return Math.random();
  };

  customElements.define(
    'fp-subscribe-popup',
    class extends HTMLElement {
      connectedCallback() {
        // Shopify's "not a robot" page: keep out of the way and keep any
        // pending sign-up for when the visitor comes back from it.
        if (location.pathname.indexOf('/challenge') === 0) return;
        this.pop = this.querySelector('.fp-pop');
        this.card = this.querySelector('.fp-offer');
        this.panel = this.querySelector('[data-fp-pop-panel]');
        this.dial = this.querySelector('[data-fp-dial]');
        this.tab = this.querySelector('[data-fp-pop-reopen]');
        this.tabLabel = this.querySelector('[data-fp-tab-label]');
        this.native = this.querySelector('#fp-offer-native');
        this.prizes = [];
        try {
          this.prizes = JSON.parse(this.querySelector('[data-fp-prizes]').textContent);
        } catch (error) { /* leave the wheel without prizes */ }
        this.data = load();
        this.wheelOn = this.dataset.wheel === 'true';

        this.pop?.addEventListener('click', (event) => {
          if (event.target.closest('[data-fp-pop-close]')) this.close();
          if (event.target.closest('[data-fp-pop-copy]')) this.copy(event.target.closest('[data-fp-pop-copy]'));
          if (event.target.closest('[data-fp-spin]')) this.spin();
          if (event.target.closest('[data-fp-hub]')) this.onHub();
        });
        this.pop?.addEventListener('submit', (event) => this.onSubmit(event));
        this.tab?.addEventListener('click', () => this.reopen());
        document.addEventListener('keydown', this.onKey);

        if (this.resumeNativePost()) return;

        if (this.data.state === 'legacy') return;
        this.showTab();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKey);
      }

      onKey = (event) => {
        if (event.key === 'Escape' && this.isOpen() && !this.spinning) this.close();
      };

      isOpen() {
        return Boolean(this.pop && !this.pop.hidden);
      }

      /* ---- What to show ------------------------------------------------ */

      setMode(mode) {
        if (this.card) this.card.dataset.mode = mode;
      }

      render(name) {
        var tpl = this.querySelector('[data-fp-tpl="' + name + '"]');
        if (!tpl || !this.panel) return;
        this.panel.replaceChildren(tpl.content.cloneNode(true));
      }

      showWheelForm() {
        this.setMode('wheel');
        this.render('wheel-form');
      }

      showSpin() {
        this.setMode('wheel');
        this.render('wheel-spin');
      }

      showResult(prize, onWheel) {
        this.setMode(onWheel ? 'wheel' : 'ten');
        this.render('result');
        if (!prize) return;
        var set = (selector, text) => {
          var el = this.panel.querySelector(selector);
          if (el) el.textContent = text;
        };
        set('[data-fp-result-eyebrow]', onWheel ? "You've won" : "You're on the list");
        set('[data-fp-result-title]', prize.title);
        set('[data-fp-result-text]', prize.text);
        set('[data-fp-pop-code]', prize.code);
        if (onWheel) this.restDial(this.prizes.indexOf(prize));
        this.applyPrize(prize);
      }

      showTen() {
        this.setMode('ten');
        this.render('ten-form');
      }

      /* ---- Opening and closing ---------------------------------------- */

      open() {
        if (!this.pop) return;
        if (!this.isOpen()) window.fpAnalytics?.track('offer_open');
        // Start Shopify's captcha now, while they type, so it is ready to submit.
        if (this.native && this.panel && this.panel.querySelector('[data-fp-offer-form]')) this.captchaReady();
        this.hideTab();
        this.pop.hidden = false;
        this.pop.removeAttribute('aria-hidden');
        requestAnimationFrame(() => requestAnimationFrame(() => this.pop.setAttribute('data-open', '')));
        window.setTimeout(() => {
          var focusable = this.panel && this.panel.querySelector('input, [data-fp-spin], [data-fp-pop-copy]');
          if (focusable) focusable.focus({ preventScroll: true });
        }, 320);
      }

      close() {
        if (this.spinning) return;
        if (this.isOpen()) window.fpAnalytics?.track('offer_dismiss');
        if (this.data.state === 'new') {
          this.data = { state: 'dismissed' };
          save(this.data);
        }
        this.hideModal(() => this.showTab());
      }

      // The bookmark opens what fits where this visitor has got to.
      reopen() {
        var state = this.data.state;
        // The wheel is off: what the wheel gave this browser (a spin or a prize)
        // is not shown again; the badge offers the 10% sign-up.
        var fromWheel = state === 'subscribed' || (state === 'won' && this.data.via === 'wheel');
        if (fromWheel && !this.wheelOn) {
          this.showTen();
        } else if (state === 'won') {
          var prize = this.prizes.find((p) => p.id === this.data.prize);
          this.showResult(prize, this.data.via === 'wheel');
        } else if (state === 'subscribed' && this.wheelOn) {
          this.showSpin();
        } else if (state === 'subscribed') {
          // Signed up for a spin while the wheel was on: give them the 10%.
          this.afterSubscribe('ten');
        } else if (this.wheelOn) {
          this.showWheelForm();
        } else {
          this.showTen();
        }
        this.open();
      }

      hideModal(after) {
        if (!this.pop) {
          if (after) after();
          return;
        }
        this.pop.removeAttribute('data-open');
        var finish = () => {
          this.pop.hidden = true;
          this.pop.setAttribute('aria-hidden', 'true');
          if (after) after();
        };
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
        else window.setTimeout(finish, 260);
      }

      showTab() {
        if (!this.tab || this.data.state === 'legacy') return;
        var fromWheel = this.data.state === 'subscribed' || (this.data.state === 'won' && this.data.via === 'wheel');
        var labels = { won: 'Your code', subscribed: 'Spin to win' };
        if (fromWheel && !this.wheelOn) labels = {};
        if (this.tabLabel) this.tabLabel.textContent = labels[this.data.state] || '10% off';
        this.tab.hidden = false;
        requestAnimationFrame(() => this.tab.setAttribute('data-visible', ''));
      }

      hideTab() {
        if (!this.tab) return;
        this.tab.removeAttribute('data-visible');
        this.tab.hidden = true;
      }

      /* ---- Subscribing ------------------------------------------------- */

      async onSubmit(event) {
        var form = event.target.closest('[data-fp-offer-form]');
        if (!form) return;
        event.preventDefault();
        var input = form.querySelector('input[type="email"]');
        var email = input && input.value ? input.value.trim() : '';
        if (!email || (input && !input.checkValidity())) {
          this.showError(form, 'Enter a valid email address.');
          return;
        }
        var mode = this.card ? this.card.dataset.mode : 'wheel';
        this.showError(form, '');
        var button = form.querySelector('[type="submit"]');
        var label = button ? button.textContent : '';
        if (button) {
          button.disabled = true;
          button.textContent = 'Signing you up';
        }

        var result = await this.subscribe(email, mode);
        if (result === 'redirect') return;
        if (result !== 'ok') {
          this.showError(form, "We couldn't sign you up just then. Check the address and try again.");
          if (button) {
            button.disabled = false;
            button.textContent = label;
          }
          return;
        }
        this.afterSubscribe(mode, this.drawnPrize);
      }

      // Shopify only accepts the form with its invisible captcha token, which
      // its own script adds when the form is submitted for real. So the form
      // is posted normally and the offer picks up when the page comes back.
      async subscribe(email, mode) {
        if (!this.native) return 'error';
        this.native.querySelector('[name="contact[email]"]').value = email;
        // The prize is drawn now and tagged on the customer ("wheel-15"), so
        // each discount can be limited in Shopify to customers with its tag:
        // a code copied out of the page then works for nobody else. The
        // wheel lands on this prize after the page comes back.
        var prize = mode === 'wheel' ? (this.prizes[this.pick()] || {}).id || '' : '10';
        this.drawnPrize = prize;
        this.native.querySelector('[name="contact[tags]"]').value =
          (mode === 'wheel' ? 'newsletter, spin-wheel' : 'newsletter, popup-10') + (prize ? ', wheel-' + prize : '');
        try {
          sessionStorage.setItem(PENDING_KEY, mode + ':' + prize);
        } catch (error) {
          return 'error';
        }
        await this.captchaReady();
        var form = this.native;
        return new Promise((resolve) => {
          window.addEventListener('pagehide', () => resolve('redirect'), { once: true });
          // Still here long after submitting: the captcha never let it through.
          window.setTimeout(() => {
            try {
              sessionStorage.removeItem(PENDING_KEY);
            } catch (error) { /* nothing to clear */ }
            resolve('error');
          }, 45000);
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        });
      }

      // Shopify blocks the submit until its captcha is bound to the form.
      captchaReady() {
        var form = this.native;
        var bound = () => form.dataset.hcaptchaBound || form.dataset.recaptchaBound;
        if (bound()) return Promise.resolve();
        try {
          var captcha = window.Shopify && window.Shopify.captcha;
          if (captcha && captcha.protect && !form.dataset.cptcha) captcha.protect(form, function () {});
        } catch (error) { /* fall through to waiting */ }
        var started = Date.now();
        return new Promise((resolve) => {
          var check = () => {
            if (bound() || Date.now() - started > 8000) resolve();
            else window.setTimeout(check, 150);
          };
          check();
        });
      }

      afterSubscribe(mode, prize) {
        if (mode === 'wheel') {
          this.data = { state: 'subscribed', prize: prize || undefined };
          save(this.data);
          this.showSpin();
        } else {
          var prize = this.prizes.find((p) => p.id === '10');
          this.data = { state: 'won', prize: '10', via: 'ten' };
          save(this.data);
          this.showResult(prize, false);
        }
        var focusable = this.panel && this.panel.querySelector('[data-fp-spin], [data-fp-pop-copy]');
        if (focusable) focusable.focus({ preventScroll: true });
      }

      // Back from posting the form: Shopify marks it as posted, or with errors.
      resumeNativePost() {
        var pending = '';
        try {
          pending = sessionStorage.getItem(PENDING_KEY) || '';
          sessionStorage.removeItem(PENDING_KEY);
        } catch (error) { /* nothing to resume */ }
        if (!pending) return false;
        // "wheel:15": the mode, and the prize drawn when the form was sent.
        var mode = pending.split(':')[0];
        var prize = pending.split(':')[1] || '';
        var posted = this.querySelector('[data-fp-native-ok]') || location.search.indexOf('customer_posted=true') !== -1;
        if (posted) {
          if (this.querySelector('[data-fp-native-ok]')) window.fpAnalytics?.track('newsletter_signup');
          this.afterSubscribe(mode, prize);
          this.open();
          return true;
        }
        if (mode === 'wheel') this.showWheelForm();
        else this.showTen();
        var form = this.panel && this.panel.querySelector('[data-fp-offer-form]');
        if (form) this.showError(form, "We couldn't sign you up just then. Check the address and try again.");
        this.open();
        return true;
      }

      showError(form, message) {
        var el = form.querySelector('[data-fp-pop-error]');
        if (!el) return;
        el.hidden = !message;
        el.textContent = message;
      }

      /* ---- The wheel --------------------------------------------------- */

      // Hand the prize to the basket: apply the code and, for a product prize,
      // add it once. The result is said in the panel's status line.
      async applyPrize(prize) {
        var helper = window.FpWheelPrize;
        var panel = this.panel;
        if (!helper || !prize) return;
        var first = !this.data.added;
        var result = await helper.apply(prize, { addItem: first });
        if (result.item === 'added') {
          this.data.added = true;
          save(this.data);
        }
        helper.describe(panel, prize, result);
      }

      // The logo in the middle of the wheel acts as its spin button.
      onHub() {
        if (this.spinning) return;
        var state = this.data.state;
        if (state === 'subscribed') {
          this.spin();
          return;
        }
        if (state === 'won') {
          this.showTip('You have already used your spin.');
          return;
        }
        if (state === 'legacy') {
          this.showTip('You have already claimed your welcome offer.');
          return;
        }
        this.showTip('Submit your email to unlock your spin');
        var input = this.panel && this.panel.querySelector('input[type="email"]');
        if (!input) return;
        input.focus();
        input.classList.remove('is-nudged');
        void input.offsetWidth;
        input.classList.add('is-nudged');
      }

      showTip(text) {
        var tip = this.querySelector('[data-fp-tip]');
        if (!tip) return;
        tip.textContent = text;
        tip.hidden = false;
        window.clearTimeout(this.tipTimer);
        this.tipTimer = window.setTimeout(() => {
          tip.hidden = true;
        }, 3400);
      }

      pick() {
        var total = this.prizes.reduce((sum, p) => sum + (Number(p.weight) || 0), 0);
        var roll = randomUnit() * total;
        for (var i = 0; i < this.prizes.length; i++) {
          roll -= Number(this.prizes[i].weight) || 0;
          if (roll < 0) return i;
        }
        return 0;
      }

      spin() {
        if (this.spinning || this.data.state !== 'subscribed' || !this.prizes.length) return;
        // Drawn and tagged at sign-up; older sign-ups draw now.
        var index = this.prizes.findIndex((p) => p.id === this.data.prize);
        if (index < 0) index = this.pick();
        var prize = this.prizes[index];
        // Saved before the wheel moves, so a reload cannot buy a second spin.
        this.data = { state: 'won', prize: prize.id, via: 'wheel' };
        save(this.data);
        this.spinning = true;

        var button = this.panel && this.panel.querySelector('[data-fp-spin]');
        if (button) {
          button.disabled = true;
          button.textContent = 'Spinning';
        }

        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var segment = 360 / this.prizes.length;
        var jitter = (randomUnit() - 0.5) * segment * 0.6;
        var turns = reduce ? 1 : 6;
        var target = turns * 360 + (360 - index * segment) + jitter;
        var duration = reduce ? 900 : 5200;

        if (this.dial) {
          this.dial.style.transition = 'none';
          this.dial.style.transform = 'rotate(0deg)';
          void this.dial.offsetWidth;
          this.dial.style.transition = 'transform ' + duration + 'ms cubic-bezier(0.12, 0.7, 0.1, 1)';
          this.dial.style.transform = 'rotate(' + target + 'deg)';
        }

        window.setTimeout(() => {
          this.spinning = false;
          var seg = this.querySelector('[data-seg="' + index + '"]');
          if (seg) seg.classList.add('is-won');
          window.setTimeout(() => {
            this.showResult(prize, true);
            var copy = this.panel && this.panel.querySelector('[data-fp-pop-copy]');
            if (copy) copy.focus({ preventScroll: true });
          }, reduce ? 0 : 450);
        }, duration);
      }

      // Show the winning segment under the pointer when the code is reopened.
      restDial(index) {
        if (!this.dial || index < 0) return;
        if (this.dial.style.transform) return;
        this.dial.style.transition = 'none';
        this.dial.style.transform = 'rotate(' + (360 - index * (360 / this.prizes.length)) + 'deg)';
        var seg = this.querySelector('[data-seg="' + index + '"]');
        if (seg) seg.classList.add('is-won');
      }

      async copy(button) {
        var codeEl = this.querySelector('[data-fp-pop-code]');
        var code = ((codeEl && codeEl.textContent) || '').trim();
        var label = button.textContent;
        try {
          await navigator.clipboard.writeText(code);
          button.textContent = 'Copied';
        } catch (error) {
          button.textContent = 'Select and copy';
        }
        window.setTimeout(() => {
          button.textContent = label;
        }, 2200);
      }
    }
  );
})();
